import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { getMyAdminStatus } from "@/lib/billing.functions";

/**
 * Dual-runtime deploy badge.
 *
 * Two services run this app:
 *   1. Cloudflare Worker — frontend + server functions. Build marker is a
 *      timestamp captured on first request after cold start (see
 *      `getDeployedVersion`).
 *   2. Render Node host — pipeline executor. Build marker is the commit
 *      SHA injected at Docker build time (`BUILD_SHA` in render.yaml).
 *
 * The badge surfaces both so a "deploy in progress" state on EITHER side
 * is visible. If `VITE_PIPELINE_HEALTH_URL` is unset (pre-cutover), the
 * Node check is skipped and the badge behaves as before.
 */

declare const __BUILD_TIME__: string;
const CLIENT_BUILD_TIME = (() => {
  try {
    return __BUILD_TIME__;
  } catch {
    return new Date(0).toISOString();
  }
})();

const PIPELINE_HEALTH_URL =
  (import.meta.env.VITE_PIPELINE_HEALTH_URL as string | undefined) ?? "";

// Edge function /health for the DB proxy. Verifies the second half of the
// system (Render → edge proxy → DB) is alive. Auto-derived from the
// Supabase URL; override with VITE_PIPELINE_PROXY_HEALTH_URL if needed.
const PROXY_HEALTH_URL: string = (() => {
  const override = import.meta.env.VITE_PIPELINE_PROXY_HEALTH_URL as
    | string
    | undefined;
  if (override) return override;
  const sb = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  return sb ? `${sb.replace(/\/+$/, "")}/functions/v1/pipeline-db-proxy/health` : "";
})();

type Status = "checking" | "in-sync" | "deploying" | "unknown";

type NodeHealth = {
  ok: boolean;
  build_sha: string;
  last_poll_at: string | null;
  active_run_id: string | null;
} | null;

type ProxyHealthState = {
  ok: boolean;
  build_sha: string;
  deployed_at: string;
} | null;

export function DeployStatusBadge() {
  const { user } = useAuth();
  const fetchAdmin = useServerFn(getMyAdminStatus);
  const adminQuery = useQuery({
    queryKey: ["my-admin", user?.id],
    queryFn: () => fetchAdmin(),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });
  const isAdmin = !!adminQuery.data?.isAdmin;

  const [workerStatus, setWorkerStatus] = useState<Status>("checking");
  const [workerVersion, setWorkerVersion] = useState<string | null>(null);
  const [nodeStatus, setNodeStatus] = useState<Status>(
    PIPELINE_HEALTH_URL ? "checking" : "unknown",
  );
  const [nodeHealth, setNodeHealth] = useState<NodeHealth>(null);
  const [proxyStatus, setProxyStatus] = useState<Status>(
    PROXY_HEALTH_URL ? "checking" : "unknown",
  );
  const [proxyHealth, setProxyHealth] = useState<ProxyHealthState>(null);

  useEffect(() => {
    let cancelled = false;
    async function checkWorker() {
      try {
        const res = await fetch("/api/public/pipeline-tick?version", {
          cache: "no-store",
        });
        const j = (await res.json()) as { deployed_version?: string };
        if (cancelled) return;
        const sv = j.deployed_version ?? null;
        setWorkerVersion(sv);
        if (!sv) {
          setWorkerStatus("unknown");
          return;
        }
        const serverMs = new Date(sv).getTime();
        const clientMs = new Date(CLIENT_BUILD_TIME).getTime();
        setWorkerStatus(serverMs >= clientMs ? "in-sync" : "deploying");
      } catch {
        if (!cancelled) setWorkerStatus("unknown");
      }
    }
    async function checkNode() {
      if (!PIPELINE_HEALTH_URL) return;
      try {
        const res = await fetch(PIPELINE_HEALTH_URL, { cache: "no-store" });
        const j = (await res.json()) as {
          ok?: boolean;
          build_sha?: string;
          last_poll_at?: string | null;
          active_run_id?: string | null;
        };
        if (cancelled) return;
        const h: NodeHealth = {
          ok: !!j.ok,
          build_sha: j.build_sha ?? "unknown",
          last_poll_at: j.last_poll_at ?? null,
          active_run_id: j.active_run_id ?? null,
        };
        setNodeHealth(h);
        // "Healthy" if we got a 200 with a recent poll (<60s).
        const pollMs = h.last_poll_at ? Date.parse(h.last_poll_at) : 0;
        const fresh = pollMs && Date.now() - pollMs < 60_000;
        setNodeStatus(h.ok && fresh ? "in-sync" : "deploying");
      } catch {
        if (!cancelled) setNodeStatus("unknown");
      }
    }
    async function checkProxy() {
      if (!PROXY_HEALTH_URL) return;
      try {
        const res = await fetch(PROXY_HEALTH_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = (await res.json()) as {
          ok?: boolean;
          build_sha?: string;
          deployed_at?: string;
        };
        if (cancelled) return;
        const h: ProxyHealthState = {
          ok: !!j.ok,
          build_sha: j.build_sha ?? "unknown",
          deployed_at: j.deployed_at ?? "",
        };
        setProxyHealth(h);
        setProxyStatus(h.ok ? "in-sync" : "deploying");
      } catch {
        // Graceful: badge says "proxy unreachable" rather than breaking.
        if (!cancelled) {
          setProxyHealth(null);
          setProxyStatus("unknown");
        }
      }
    }
    checkWorker();
    checkNode();
    checkProxy();
    const id = setInterval(() => {
      checkWorker();
      checkNode();
      checkProxy();
    }, 15_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Roll all configured statuses into one overall tone. Each enabled check
  // (worker, node pipeline, edge proxy) contributes; "unknown" from a
  // configured check degrades to amber so an unreachable proxy is visible.
  const enabled: Status[] = [
    workerStatus,
    ...(PIPELINE_HEALTH_URL ? [nodeStatus] : []),
    ...(PROXY_HEALTH_URL ? [proxyStatus] : []),
  ];
  const overall: Status = enabled.every((s) => s === "in-sync")
    ? "in-sync"
    : enabled.some((s) => s === "deploying")
      ? "deploying"
      : enabled.some((s) => s === "checking")
        ? "checking"
        : "unknown";

  const label =
    !PIPELINE_HEALTH_URL && !PROXY_HEALTH_URL
      ? overall === "checking"
        ? "Checking deploy…"
        : overall === "in-sync"
          ? "In sync with deploy"
          : overall === "deploying"
            ? "Deploy in progress…"
            : "Deploy state unknown"
      : [
          `Worker ${shortStatus(workerStatus)}`,
          PIPELINE_HEALTH_URL ? `Pipeline ${shortStatus(nodeStatus)}` : null,
          PROXY_HEALTH_URL
            ? `Proxy ${proxyStatus === "unknown" ? "✕" : shortStatus(proxyStatus)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ");

  const tone =
    overall === "in-sync"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      : overall === "deploying"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
        : "border-border bg-card text-muted-foreground";

  const Icon =
    overall === "in-sync"
      ? CheckCircle2
      : overall === "deploying" || overall === "checking"
        ? Loader2
        : AlertTriangle;

  const fmt = (s: string | null) =>
    s ? new Date(s).toLocaleTimeString() : "—";

  const title = [
    `Client build:  ${fmt(CLIENT_BUILD_TIME)}`,
    `Worker deploy: ${fmt(workerVersion)}`,
    PIPELINE_HEALTH_URL
      ? `Pipeline SHA:  ${nodeHealth?.build_sha ?? "—"}`
      : null,
    PIPELINE_HEALTH_URL
      ? `Last poll:     ${fmt(nodeHealth?.last_poll_at ?? null)}`
      : null,
    PIPELINE_HEALTH_URL && nodeHealth?.active_run_id
      ? `Active run:    ${nodeHealth.active_run_id}`
      : null,
    PROXY_HEALTH_URL
      ? `Proxy SHA:     ${proxyHealth?.build_sha ?? "unreachable"}`
      : null,
    PROXY_HEALTH_URL
      ? `Proxy deploy:  ${fmt(proxyHealth?.deployed_at ?? null)}`
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  if (!user || !isAdmin) return null;

  return (
    <div
      className={`fixed bottom-3 right-3 z-50 flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs shadow-sm ${tone}`}
      title={title}
    >
      <Icon
        className={`h-3.5 w-3.5 ${overall === "deploying" || overall === "checking" ? "animate-spin" : ""}`}
      />
      <span>{label}</span>
    </div>
  );
}

function shortStatus(s: Status): string {
  return s === "in-sync"
    ? "✓"
    : s === "deploying"
      ? "…"
      : s === "checking"
        ? "?"
        : "!";
}